/**
 * Swamithra — the domain error codes.
 *
 * API_STYLE_GUIDE.md §5: clients branch on `code`, never on `message`. These are
 * the codes this product's services return beyond the generic ones, and the copy
 * each of them shows.
 *
 * WHY THE COPY IS HERE AND NOT ONLY ON THE SERVER. Several acceptance criteria
 * name the message a user must see — 'code expired, request a new one'
 * (US-00-001.8), 'could not send the code, try again' (US-00-001.22), the
 * lockout message with its remaining time (US-01-005.9). A message that exists
 * only in a Go handler is a message three clients cannot test. The server still
 * sends one, and it is used for any code not listed here.
 *
 * This module holds DATA ONLY — no import of ApiError, so `./index` can depend
 * on it without a cycle.
 */

import {
	OTP_LOCKOUT_MINUTES,
	OTP_MAX_VERIFICATION_ATTEMPTS,
	OTP_VALIDITY_MINUTES,
} from "../domain/constants"

/**
 * Every domain code a client is expected to recognise.
 *
 * A code not in this list is not an error — it falls through to the server's
 * message and the status-based title, which is the correct behaviour for a
 * service that has added a case this client predates.
 */
export const ERROR_CODES = {
	// ---- Passwordless authentication (US-00-001, US-01-005) ------------------
	/** The six digits were wrong. The attempt counter has been incremented. */
	otpInvalid: "otp_invalid",
	/** Issued more than 10 minutes ago; invalidated, and NOT counted as an attempt. */
	otpExpired: "otp_expired",
	/** No live code for this number — a verify with nothing outstanding. */
	otpNotFound: "otp_not_found",
	/** Three wrong codes; the NUMBER is locked, on every device and platform. */
	otpLocked: "account_locked",
	/** Resend requested inside the cooldown. */
	otpResendCooldown: "resend_cooldown",
	/** The per-number hourly resend cap was reached. */
	otpResendLimited: "resend_limit",
	/** WhatsApp dispatch failed after its retries. */
	otpDispatchFailed: "otp_dispatch_failed",
	/** Panel login: unknown number OR deactivated Expert. Deliberately the same. */
	notAuthorised: "not_authorised",
	/** The session was idle past its window, was revoked, or its Expert is inactive. */
	sessionExpired: "session_invalid",
	/** The number already belongs to an Expert (US-01-005.15). */
	duplicateMobileNumber: "expert_exists",

	// ---- Ownership and access (US-00-006) ------------------------------------
	/**
	 * Returned for another user's record AND for a record that does not exist,
	 * with an identical body, so a response cannot be used to confirm that an id
	 * is real (US-00-006.19).
	 */
	accessDenied: "forbidden",

	// ---- Lifecycle conflicts (US-00-002, US-01-003, US-01-004) ---------------
	/** A message or feedback was posted to a Closed query. */
	queryClosed: "query_closed",
	/**
	 * An Expert's response draft changed since it was loaded: another Expert
	 * saved over it, or sent it. Nothing was overwritten; reload the draft.
	 */
	draftConflict: "draft_conflict",
	/** Feedback already exists on this query. */
	feedbackAlreadySubmitted: "feedback_exists",
	/** Feedback was submitted for a query that is not Answered. */
	queryNotAnswered: "not_answered",
	/** Close was called on a query that is not Answered, or is already Closed. */
	invalidStatusTransition: "invalid_status_transition",
	/** A second review was submitted for a vetting request. */
	reviewAlreadyPosted: "already_reviewed",

	// ---- Uploads (US-00-002.2, US-00-004.3) ----------------------------------
	/**
	 * ONE CODE FOR ALL THREE UPLOAD FAILURES. The service answers a sniffed type
	 * it does not accept, a file over 5 MB and a spoofed extension with
	 * `invalid_file` and a message naming the rule that was broken; the count
	 * ceiling is the one that has its own code. The client's own check
	 * (../files) is what produces a per-rule message before a byte is sent, so
	 * nothing is lost by the server being terser than this list once was.
	 */
	invalidFile: "invalid_file",
	tooManyFiles: "too_many_files",

	// ---- Bodies the service refuses outright ---------------------------------
	/** A field the endpoint does not accept, or a body it could not parse. */
	invalidBody: "invalid_body",
	/** A PATCH that named nothing to change. */
	nothingToUpdate: "nothing_to_update",
	/** An empty message, response or review (US-01-004.8). */
	emptyMessage: "empty_message",
	emptyResponse: "empty_response",
	emptyReview: "empty_review",
	/** An OTP request for an unknown number with no profile details attached. */
	registrationMissing: "registration_missing",
} as const

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES]

/**
 * The copy shown for each code.
 *
 * Written for the person reading it, not for the developer reading the log. The
 * lockout entry is a fallback: when the response carries a `Retry-After`, the
 * caller uses `lockoutMessage` instead so the user is told when, not just that.
 */
export const ERROR_MESSAGES: Record<string, string> = {
	[ERROR_CODES.otpInvalid]: "That OTP is not correct. Check it and try again.",
	[ERROR_CODES.otpExpired]: `That OTP has expired after ${OTP_VALIDITY_MINUTES} minutes. Request a new one.`,
	[ERROR_CODES.otpLocked]: `This account is locked after ${OTP_MAX_VERIFICATION_ATTEMPTS} incorrect OTPs. Try again in ${OTP_LOCKOUT_MINUTES} minutes.`,
	[ERROR_CODES.otpResendCooldown]: "Hold on a moment before requesting another OTP.",
	[ERROR_CODES.otpResendLimited]:
		"Too many OTPs have been requested for this number. Try again later.",
	[ERROR_CODES.otpDispatchFailed]: "We could not send the OTP. Try again.",
	[ERROR_CODES.otpNotFound]: "That OTP is no longer valid. Request a new one.",
	[ERROR_CODES.notAuthorised]: "This number is not authorised for the panel",
	[ERROR_CODES.sessionExpired]: "Your session has expired. Verify your number to continue.",
	[ERROR_CODES.duplicateMobileNumber]: "An expert with this WhatsApp number already exists.",
	[ERROR_CODES.accessDenied]: "You do not have access to this.",
	[ERROR_CODES.queryClosed]: "This query is closed. Raise a new query to continue.",
	[ERROR_CODES.feedbackAlreadySubmitted]: "You have already rated this answer.",
	[ERROR_CODES.queryNotAnswered]: "This query has not been answered yet.",
	[ERROR_CODES.invalidStatusTransition]: "This has already moved on. Refresh to see its status.",
	[ERROR_CODES.reviewAlreadyPosted]: "A review has already been posted for this request.",
	[ERROR_CODES.invalidFile]: "That file cannot be attached.",
	[ERROR_CODES.tooManyFiles]: "Too many files were attached.",
	[ERROR_CODES.emptyMessage]: "Write a message before sending.",
	[ERROR_CODES.emptyResponse]: "Write a response before sending.",
	[ERROR_CODES.emptyReview]: "Write your feedback before submitting.",
	[ERROR_CODES.registrationMissing]: "Enter your full name and user type to continue.",
	[ERROR_CODES.nothingToUpdate]: "Change something before saving.",
}

/**
 * The lockout message with its remaining time (US-00-001.2, US-01-005.9).
 *
 * Rounds UP: telling someone to wait 29 minutes when 29 minutes and 40 seconds
 * remain produces a second failed attempt and a second complaint.
 */
export function lockoutMessage(retryAfterSeconds: number | undefined): string {
	if (retryAfterSeconds === undefined || retryAfterSeconds <= 0) {
		return ERROR_MESSAGES[ERROR_CODES.otpLocked] ?? ""
	}
	const minutes = Math.ceil(retryAfterSeconds / 60)
	const when = minutes === 1 ? "1 minute" : `${minutes} minutes`
	return `This account is locked after too many incorrect OTPs. Try again in ${when}.`
}

/**
 * How long until the resend control may be enabled again.
 *
 * The server's `resendAvailableAt` is the authority; this only turns it into
 * seconds for a countdown (US-00-001.10, US-01-005.10).
 */
export function secondsUntil(iso: string, now = new Date()): number {
	const target = new Date(iso).getTime()
	if (Number.isNaN(target)) return 0
	return Math.max(0, Math.ceil((target - now.getTime()) / 1000))
}
